// src/components/DashboardSection.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { Bar, Pie } from 'react-chartjs-2';
import { toast } from 'react-toastify';

// --- START Chart Components (Internal to Dashboard) ---
function SixMonthComparisonChart({ chartData }) {
    const data = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Total Business', data: chartData.totalBusiness,
                borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.5)',
                type: 'line', tension: 0.4, yAxisID: 'y1',
            },
            { label: 'Income', data: chartData.totalReceived, backgroundColor: 'rgba(22, 163, 74, 0.8)', yAxisID: 'y' },
            { label: 'Expense', data: chartData.totalExpenses, backgroundColor: 'rgba(239, 68, 68, 0.8)', yAxisID: 'y' },
        ],
    };
    const options = {
        responsive: true, interaction: { mode: 'index', intersect: false, },
        plugins: { legend: { position: 'top' }, title: { display: true, text: '6-Month Business vs Income vs Expense' } },
        scales: {
            y: { type: 'linear', display: true, position: 'left', beginAtZero: true },
            y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, beginAtZero: true },
        },
    };
    return <Bar options={options} data={data} />;
}

function ExpenseBreakdownChart({ data }) {
    const chartData = {
        labels: data.map(d => d.category),
        datasets: [{
            data: data.map(d => d.amount),
            backgroundColor: ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#F97316', '#84CC16'],
            borderColor: '#FFFFFF', borderWidth: 2,
        }]
    };
    const options = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'right' }, title: { display: true, text: 'Expense Breakdown', font: { size: 16 } } }
    };
    return <Pie data={chartData} options={options} />;
}
// --- END Chart Components ---


function DashboardSection() {
    const navigate = useNavigate();
    const { currentStationId, allStations } = useAppContext();
    const [financials, setFinancials] = useState({ totalBusiness: 0, totalReceived: 0, pending: 0, totalExpenses: 0, net: 0 });
    const [summaryCounts, setSummaryCounts] = useState({ clients: 0, activeCampaigns: 0, inactiveCampaigns: 0, employees: 0 });
    
    // --- NEW STATE FOR REMINDERS ---
    const [expiringCampaigns, setExpiringCampaigns] = useState([]);
    const [pendingBills, setPendingBills] = useState([]);
    const [alertView, setAlertView] = useState('bills'); // 'bills' or 'campaigns'

    const [expenseChartData, setExpenseChartData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (currentStationId === 'all') {
            setIsLoading(false);
            return;
        }
        if (!currentStationId) return;

        setIsLoading(true);

        const fetchData = async () => {
            try {
                // --- 1. Fetch Summary Counts & Reminders Data ---
                const sevenDaysFromNow = new Date();
                sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                const today = new Date();

                const [clients, activeCampaigns, inactiveCampaigns, employees, expiringCampaigns, pendingBills] = await Promise.all([
                    api.get('clients', { where: { stationId: currentStationId } }),
                    api.get('ad_campaigns', { where: { stationId: currentStationId, isActive: true } }),
                    api.get('ad_campaigns', { where: { stationId: currentStationId, isActive: false } }),
                    api.get('workers', { where: { stationId: currentStationId } }),
                    api.get('ad_campaigns', { where: { stationId: currentStationId, isActive: true } }),
                    api.get('bills', { where: { stationId: currentStationId, status: 'Pending' } })
                ]);

                setSummaryCounts({ 
                    clients: clients.length, 
                    activeCampaigns: activeCampaigns.length, 
                    inactiveCampaigns: inactiveCampaigns.length, 
                    employees: employees.length 
                });

                setExpiringCampaigns(
                    expiringCampaigns
                        .filter(c => c.endDate && new Date(c.endDate) <= sevenDaysFromNow && new Date(c.endDate) >= today)
                );
                
                setPendingBills(
                    pendingBills
                        .filter(b => b.dueDate && new Date(b.dueDate) <= sevenDaysFromNow)
                        .sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate))
                );

                // --- 2. Fetch data for selected month's KPIs & Expense Chart ---
                const startOfMonth = new Date(filterYear, filterMonth - 1, 1);
                const endOfMonth = new Date(filterYear, filterMonth, 0, 23, 59, 59);
                
                const [monthlyCampaigns, monthlyExpenses] = await Promise.all([
                    api.get('ad_campaigns', { where: { stationId: currentStationId } }),
                    api.get('expenses', { where: { stationId: currentStationId } })
                ]);

                const filteredMonthlyCampaigns = monthlyCampaigns.filter(c => {
                    const d = new Date(c.startDate);
                    return d >= startOfMonth && d <= endOfMonth;
                });

                const filteredMonthlyExpenses = monthlyExpenses.filter(e => {
                    const d = new Date(e.date);
                    return d >= startOfMonth && d <= endOfMonth;
                });

                const expenseByCategory = filteredMonthlyExpenses.reduce((acc, expense) => {
                    const category = expense.category || 'Miscellaneous';
                    acc[category] = (acc[category] || 0) + (expense.amount || 0);
                    return acc;
                }, {});
                setExpenseChartData(Object.entries(expenseByCategory).map(([category, amount]) => ({ category, amount })));

                const monthlyCampaignIds = filteredMonthlyCampaigns.map(c => c.id).filter(Boolean);
                let monthlyPayments = [];
                if (monthlyCampaignIds.length > 0) {
                    const allPayments = await api.get('payments');
                    monthlyPayments = allPayments.filter(p => monthlyCampaignIds.includes(p.campaignId));
                }

                const totalBusiness = filteredMonthlyCampaigns.reduce((sum, c) => sum + (parseFloat(c.totalCampaignAmount) || 0), 0);
                const totalReceived = monthlyPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                const totalExpenses = filteredMonthlyExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
                setFinancials({ totalBusiness, totalReceived, pending: totalBusiness - totalReceived, totalExpenses, net: totalReceived - totalExpenses });
                
                // --- 3. Fetch data for 6-Month Chart ---
                const [allCampaigns, allExpenses, allPayments] = await Promise.all([
                    api.get('ad_campaigns', { where: { stationId: currentStationId } }),
                    api.get('expenses', { where: { stationId: currentStationId } }),
                    api.get('payments', { where: { stationId: currentStationId } })
                ]);

                const chartLabels = [], chartBusiness = [], chartReceived = [], chartExpenses = [];
                for (let i = 5; i >= 0; i--) {
                    const d = new Date(); d.setMonth(d.getMonth() - i);
                    const y = d.getFullYear(); const m = d.getMonth();
                    chartLabels.push(d.toLocaleString('en-US', { month: 'short' }));
                    
                    const monthBusiness = allCampaigns
                        .filter(c => c.startDate && new Date(c.startDate).getFullYear() === y && new Date(c.startDate).getMonth() === m)
                        .reduce((s, c) => s + (parseFloat(c.totalCampaignAmount) || 0), 0);
                    
                    const monthReceived = allPayments
                        .filter(p => p.date && new Date(p.date).getFullYear() === y && new Date(p.date).getMonth() === m)
                        .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
                    
                    const monthExpenses = allExpenses
                        .filter(e => e.date && new Date(e.date).getFullYear() === y && new Date(e.date).getMonth() === m)
                        .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
                    
                    chartBusiness.push(monthBusiness); 
                    chartReceived.push(monthReceived); 
                    chartExpenses.push(monthExpenses);
                }
                setChartData({ labels: chartLabels, totalBusiness: chartBusiness, totalReceived: chartReceived, totalExpenses: chartExpenses });

            } catch (err) {
                 toast.error("Failed to load dashboard data.");
                 console.error("Dashboard Fetch Error:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [currentStationId, filterYear, filterMonth]);

    if (currentStationId === 'all') {
        return ( <div className="flex flex-col items-center justify-center text-center p-10 bg-white rounded-2xl shadow-lg h-full"><svg className="w-16 h-16 text-rose-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><h2 className="text-2xl font-bold text-gray-800">Welcome, Admin!</h2><p className="text-gray-500 mt-2">Please select a radio station from the dropdown above to view its dashboard.</p></div>);
    }
    
    if (isLoading) {
        return <div className="flex items-center justify-center p-10"><div className="text-xl font-semibold">Loading Dashboard...</div></div>;
    }

    return (
        <div className="space-y-6">
            <div className="mb-8"><h1 className="text-4xl font-bold text-gray-800">Dashboard</h1><p className="text-gray-500 mt-1">A quick overview for <span className="font-bold text-rose-600">{allStations.find(s => s.id === currentStationId)?.name}</span></p></div>

            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8"><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end"><div><label className="text-sm font-medium text-gray-600">Month</label><select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="w-full p-3 bg-gray-50 rounded-md mt-1">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select></div><div><label className="text-sm font-medium text-gray-600">Year</label><select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="w-full p-3 bg-gray-50 rounded-md mt-1">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div><div className="grid grid-cols-2 gap-4 col-span-1 lg:col-span-2"><button onClick={() => navigate('/adCampaigns')} className="w-full bg-rose-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-rose-700 transition-all">+ Add Campaign</button><button onClick={() => navigate('/expenses')} className="w-full bg-purple-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-purple-700 transition-all">+ Add Expense</button></div></div></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"><div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-rose-100 rounded-lg"><span className="text-2xl">👤</span></div><div><p className="text-sm font-medium text-gray-500">Total Clients</p><p className="text-2xl font-bold text-gray-800">{summaryCounts.clients}</p></div></div><div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-green-100 rounded-lg"><span className="text-2xl">📈</span></div><div><p className="text-sm font-medium text-gray-500">Active Campaigns</p><p className="text-2xl font-bold text-green-600">{summaryCounts.activeCampaigns}</p></div></div><div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-red-100 rounded-lg"><span className="text-2xl">📉</span></div><div><p className="text-sm font-medium text-gray-500">Inactive Campaigns</p><p className="text-2xl font-bold text-red-600">{summaryCounts.inactiveCampaigns}</p></div></div><div className="bg-white p-6 rounded-2xl shadow-lg flex items-center gap-4"><div className="p-3 bg-blue-100 rounded-lg"><span className="text-2xl">👥</span></div><div><p className="text-sm font-medium text-gray-500">Total Employees</p><p className="text-2xl font-bold text-blue-600">{summaryCounts.employees}</p></div></div></div>

            <div className="bg-white rounded-2xl shadow-lg p-6"><div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-center"><div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm font-medium text-blue-700">Total Business</p><p className="text-2xl font-bold mt-1">PKR {financials.totalBusiness.toLocaleString()}</p></div><div className="p-4 bg-green-50 rounded-lg"><p className="text-sm font-medium text-green-700">Received</p><p className="text-2xl font-bold mt-1">PKR {financials.totalReceived.toLocaleString()}</p></div><div className="p-4 bg-red-50 rounded-lg"><p className="text-sm font-medium text-red-700">Pending</p><p className="text-2xl font-bold mt-1">PKR {financials.pending.toLocaleString()}</p></div><div className="p-4 bg-purple-50 rounded-lg"><p className="text-sm font-medium text-purple-700">Expenses</p><p className="text-2xl font-bold mt-1">PKR {financials.totalExpenses.toLocaleString()}</p></div><div className={`p-4 rounded-lg ${financials.net >= 0 ? 'bg-orange-50' : 'bg-pink-100'}`}><p className={`text-sm font-medium ${financials.net >= 0 ? 'text-orange-800' : 'text-pink-800'}`}>Net Income</p><p className="text-2xl font-bold mt-1">PKR {financials.net.toLocaleString()}</p></div></div></div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* --- UPDATED REMINDER/ALERTS BOX --- */}
                <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
                    <div className="flex justify-between items-center mb-4 flex-shrink-0">
                        <h3 className="font-bold text-lg text-gray-700">Alerts & Reminders</h3>
                        <select value={alertView} onChange={e => setAlertView(e.target.value)} className="p-1 text-sm bg-gray-100 border-gray-300 rounded-md">
                            <option value="bills">Bill Reminders</option>
                            <option value="campaigns">Expiring Campaigns</option>
                        </select>
                    </div>
                    <div className="space-y-3 overflow-y-auto flex-grow h-60">
                        {alertView === 'bills' && (
                            pendingBills.length > 0 ? pendingBills.map(bill => {
                                const dueDate = new Date(bill.dueDate);
                                const today = new Date(); today.setHours(0,0,0,0);
                                const isOverdue = dueDate < today;
                                const diffTime = dueDate.getTime() - today.getTime();
                                const diffDays = Math.ceil(diffTime / (1024 * 60 * 60 * 24));
                                return (
                                    <div key={bill.id} className="p-3 rounded-lg border-l-4 bg-gray-50" style={{borderColor: isOverdue ? '#EF4444' : '#F59E0B'}}>
                                        <div className="flex justify-between items-center">
                                            <p className="font-semibold text-gray-800 text-sm">{bill.billName}</p>
                                            <button onClick={() => navigate('/billManagement')} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">View Bill</button>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 flex justify-between">
                                            <span>Due: {dueDate.toLocaleDateString()}</span>
                                            {isOverdue ? <span className="font-bold text-red-600">OVERDUE</span> : <span className="font-bold text-yellow-600">DUE IN {diffDays} DAYS</span>}
                                        </div>
                                    </div>
                                )
                            }) : <p className="text-sm text-gray-500 h-full flex items-center justify-center">No pending bills due soon.</p>
                        )}
                        {alertView === 'campaigns' && (
                             expiringCampaigns.length > 0 ? expiringCampaigns.map(camp => (
                                <div key={camp.id} className="flex justify-between items-center border-b pb-2">
                                    <p className="font-semibold text-gray-800 text-sm">{camp.mainCampaignName || camp.campaignName}</p>
                                    <p className="text-xs text-yellow-700 font-bold">{new Date(camp.endDate).toLocaleDateString()}</p>
                                </div>
                            )) : <p className="text-sm text-gray-500 h-full flex items-center justify-center">No campaigns are expiring in the next 7 days.</p>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col"><div className="flex-grow h-80 md:h-full">{chartData ? <SixMonthComparisonChart chartData={chartData} /> : <p>Loading chart data...</p>}</div></div>
                <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col"><div className="flex-grow h-80 md:h-full">{expenseChartData && expenseChartData.length > 0 ? <ExpenseBreakdownChart data={expenseChartData} /> : <p className="text-center text-gray-500 h-full flex items-center justify-center">No expense data for this month.</p>}</div></div>
            </div>
         </div>
    );
}

export default DashboardSection;
